import { useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

import type {
  JobApplicationFormFieldInput,
  JobApplicationMutationInput,
  VisibleJob,
} from '@xing-com/crate-common-graphql-types';
import { JobInteractionPlatform } from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { updateUserInteractionsOnCachedVisibleJob } from '@xing-com/crate-jobs-domain-apply-helpers';
import {
  GetInstantApplyJobDataDocument,
  SubmitInstantApplicationDocument,
} from '@xing-com/crate-jobs-graphql';
import { useLazySimilarJobs } from '@xing-com/crate-jobs-hooks';

import type {
  ApplyFormSubmitError,
  NwtSubmitErrorTrackingProps,
} from '../use-apply-form-submit-errors';
import { useApplyFormSubmitErrors } from '../use-apply-form-submit-errors';

const BAD_REQUEST_ERROR_MSG = 'Bad Request';

type UseApplyFormSubmitValue = {
  isJobDeactivated: boolean;
  isSubmitInProgress: boolean;
  submitFormData: (formData: JobApplicationFormFieldInput[]) => void;
};

export const useApplyFormSubmit = ({
  jobId,
  jobSlug,
  onSubmitSuccess,
  onBadRequestError,
  showErrorBanner,
  trackNwt,
  beforeSubmit,
}: {
  jobId: string;
  jobSlug: string;
  onSubmitSuccess: () => void;
  onBadRequestError?: () => void;
  showErrorBanner: (copyId: string) => void;
  trackNwt: (trackingProps: NwtSubmitErrorTrackingProps) => void;
  beforeSubmit?: () => Promise<void>;
}): UseApplyFormSubmitValue => {
  const [isJobDeactivated, setIsJobDeactivated] = useState<boolean>(false);
  const [currentFormData, setCurrentFormData] = useState<
    (JobApplicationFormFieldInput | undefined)[] | null
  >(null);
  const { showSubmitError } = useApplyFormSubmitErrors(
    trackNwt,
    showErrorBanner
  );
  const [submitXingApplicationMutation] = useMutation(
    SubmitInstantApplicationDocument
  );

  const { getSimilarJobs, isLoading: isSimilarJobsLoading } =
    useLazySimilarJobs({
      limit: 5,
      jobId,
    });

  const showErrorAndResetCurrentFormData = useCallback(
    (error?: ApplyFormSubmitError) => {
      if (error) {
        showSubmitError(error);
      }
      setCurrentFormData(null);
    },
    [showSubmitError]
  );
  const { isLoggedIn } = useLoginState();

  const [getJobXingApplicationData] = useLazyQuery(
    GetInstantApplyJobDataDocument,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (queryData) => {
        if (queryData?.job?.__typename !== 'VisibleJob') {
          // check job is deactivated
          showErrorAndResetCurrentFormData('deactivatedJob');
          setIsJobDeactivated(true);
        } else if (
          // oxlint-disable-next-line typescript/consistent-type-assertions
          (queryData?.job as VisibleJob)?.application?.__typename !==
          'JobXingApplication'
        ) {
          // check job apply type is not valid
          showErrorAndResetCurrentFormData('noXingApply');
        } else {
          // job application is active and has correct application type: proceed to submit the form data

          const inputData: JobApplicationMutationInput = {
            jobId,
            platform: JobInteractionPlatform.Web,
            usesDocumentHub: isLoggedIn,
            // oxlint-disable-next-line typescript/consistent-type-assertions
            formData: currentFormData as JobApplicationFormFieldInput[],
          };

          submitXingApplicationMutation({
            variables: {
              input: inputData,
            },
            onCompleted: (mutationData) => {
              if (mutationData?.jobSubmitJobApplication?.error) {
                const isBadRequestError =
                  mutationData.jobSubmitJobApplication.error?.message ===
                  BAD_REQUEST_ERROR_MSG;

                showErrorAndResetCurrentFormData(
                  isBadRequestError ? undefined : 'generic'
                );
                if (isBadRequestError && onBadRequestError) onBadRequestError();
              } else {
                onSubmitSuccess();
              }
            },
            update: (cache, { data }) => {
              if (isLoggedIn) {
                updateUserInteractionsOnCachedVisibleJob(cache, jobId, data);
              }
            },
            onError: () => {
              showErrorAndResetCurrentFormData('generic');
            },
          });
          getSimilarJobs(); // Similar jobs are requested here so that when the user hits the success page they are already available in the Apollo cache.
        }
      },
      onError: () => {
        showErrorAndResetCurrentFormData('generic');
      },
    }
  );

  const submitFormData = useCallback(
    async (formData: JobApplicationFormFieldInput[]) => {
      setCurrentFormData(formData);
      setIsJobDeactivated(false);
      await beforeSubmit?.();
      // check if current job posting is still active before data submission
      getJobXingApplicationData({ variables: { slug: jobSlug } });
    },
    [beforeSubmit, getJobXingApplicationData, jobSlug]
  );

  return {
    isJobDeactivated,
    isSubmitInProgress: !!currentFormData || isSimilarJobsLoading,
    submitFormData,
  };
};
